import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  FileText, 
  RefreshCw, 
  Download, 
  Search,
  Package,
  IndianRupee,
  Truck,
  CheckCircle2,
  Clock,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  ArrowRight,
  ShoppingCart
} from 'lucide-react';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  pending_po: { label: 'Pending PO', color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: Clock },
  po_created: { label: 'PO Created', color: 'bg-blue-100 text-blue-800 border-blue-200', icon: FileText },
  partial: { label: 'Partial Receipt', color: 'bg-orange-100 text-orange-800 border-orange-200', icon: Truck },
  complete: { label: 'Complete', color: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle2 }
};

export function ProcurementTrackingContent() {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  // Bug 4 fix: client-side pagination state. Default page_size matches the
  // backend default so the first page remains a 200-row page.
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 200;

  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchReport = async (pageArg = 1) => {
    setLoading(true);
    try {
      const params = { page: pageArg, page_size: PAGE_SIZE };
      if (statusFilter !== 'all') {
        params.status_filter = statusFilter;
      }
      const response = await axios.get(`${API_URL}/api/reports/procurement-tracking`, {
        params,
        headers: getAuthHeader()
      });
      setReportData(response.data);
    } catch (error) {
      toast.error('Failed to load procurement report');
      console.error('Report fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Reset to page 1 whenever the status filter changes, then fetch.
  useEffect(() => {
    setPage(1);
    fetchReport(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  // Refetch when page changes (but not on first render — covered by the
  // statusFilter effect above).
  useEffect(() => {
    fetchReport(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(value || 0);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), 'dd/MM/yyyy');
    } catch {
      return '-';
    }
  };

  // Filter items
  const filteredItems = reportData?.items?.filter(item => {
    const matchesSearch = 
      item.indent_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.item_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.po_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.supplier_name?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  }) || [];

  const exportToCSV = () => {
    if (!filteredItems.length) return;
    
    const headers = [
      'Indent No', 'Indent Date', 'Item Name', 'Qty Required', 'Unit', 'Linked WO',
      'PO No', 'PO Status', 'PO Date', 'Ordered Qty', 'PO Approved Price', 'PO Amount', 'Supplier',
      'Received Qty', 'Pending Qty', 'Actual Purchase Price', 'Actual Amount', 'Last Receipt',
      'Price Variance', 'Variance %', 'Status'
    ];
    
    const rows = filteredItems.map(item => [
      item.indent_number,
      formatDate(item.indent_date),
      item.item_name,
      item.qty_required,
      item.unit,
      item.linked_wo || '-',
      item.po_number || '-',
      item.po_status || '-',
      formatDate(item.po_date),
      item.po_ordered_qty,
      item.po_approved_price,
      item.po_amount,
      item.supplier_name || '-',
      item.received_qty,
      item.pending_qty,
      item.actual_purchase_price,
      item.actual_amount,
      formatDate(item.last_receipt_date),
      item.price_variance,
      item.price_variance_percent?.toFixed(2) + '%',
      statusConfig[item.procurement_status]?.label || item.procurement_status
    ]);
    
    const csv = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `procurement_tracking_${format(new Date(), 'yyyyMMdd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getVarianceColor = (variance) => {
    if (variance > 0) return 'text-red-600';
    if (variance < 0) return 'text-green-600';
    return 'text-gray-600';
  };

  return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <ShoppingCart className="w-6 h-6 text-blue-600" />
              Procurement Tracking Report
            </h1>
            <p className="text-muted-foreground mt-1">
              Track Indent → PO → Purchase Entry lifecycle with price variance
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => fetchReport(page)} disabled={loading} data-testid="procurement-refresh-btn">
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={exportToCSV} disabled={!filteredItems.length} data-testid="procurement-export-csv-btn">
              <Download className="w-4 h-4 mr-1" />
              Export CSV
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        {reportData?.summary && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            <Card>
              <CardContent className="pt-4">
                <div className="text-center">
                  <p className="text-sm text-muted-foreground">Total Indents</p>
                  <p className="text-2xl font-bold">{reportData.summary.total_indents}</p>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-yellow-200 bg-yellow-50/30">
              <CardContent className="pt-4">
                <div className="text-center">
                  <p className="text-sm text-yellow-700">Pending PO</p>
                  <p className="text-2xl font-bold text-yellow-800">{reportData.summary.pending_po}</p>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-blue-200 bg-blue-50/30">
              <CardContent className="pt-4">
                <div className="text-center">
                  <p className="text-sm text-blue-700">PO Created</p>
                  <p className="text-2xl font-bold text-blue-800">{reportData.summary.po_created}</p>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-orange-200 bg-orange-50/30">
              <CardContent className="pt-4">
                <div className="text-center">
                  <p className="text-sm text-orange-700">Partial Receipt</p>
                  <p className="text-2xl font-bold text-orange-800">{reportData.summary.partial}</p>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-green-200 bg-green-50/30">
              <CardContent className="pt-4">
                <div className="text-center">
                  <p className="text-sm text-green-700">Complete</p>
                  <p className="text-2xl font-bold text-green-800">{reportData.summary.complete}</p>
                </div>
              </CardContent>
            </Card>
            
            <Card className={`${reportData.summary.total_variance_amount > 0 ? 'border-red-200 bg-red-50/30' : 'border-green-200 bg-green-50/30'}`}>
              <CardContent className="pt-4">
                <div className="text-center">
                  <p className="text-sm text-muted-foreground">Price Variance</p>
                  <p className={`text-xl font-bold ${reportData.summary.total_variance_amount > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {formatCurrency(reportData.summary.total_variance_amount)}
                  </p>
                  <p className="text-xs text-muted-foreground">{reportData.summary.items_with_variance} items</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Value Summary */}
        {reportData?.summary && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total PO Value</p>
                    <p className="text-xl font-bold text-blue-600">{formatCurrency(reportData.summary.total_po_value)}</p>
                  </div>
                  <FileText className="w-8 h-8 text-blue-200" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Received</p>
                    <p className="text-xl font-bold text-green-600">{formatCurrency(reportData.summary.total_received_value)}</p>
                  </div>
                  <CheckCircle2 className="w-8 h-8 text-green-200" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Pending Value</p>
                    <p className="text-xl font-bold text-orange-600">{formatCurrency(reportData.summary.total_pending_value)}</p>
                  </div>
                  <Clock className="w-8 h-8 text-orange-200" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
            <Input
              placeholder="Search by indent, item, PO or supplier..."
              className="pl-9"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="pending_po">Pending PO</SelectItem>
              <SelectItem value="po_created">PO Created</SelectItem>
              <SelectItem value="partial">Partial Receipt</SelectItem>
              <SelectItem value="complete">Complete</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Results Table */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Procurement Details</CardTitle>
            <CardDescription>
              {filteredItems.length} records showing Indent → PO → Receipt flow
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="min-w-[100px]">Indent</TableHead>
                    <TableHead className="min-w-[150px]">Item</TableHead>
                    <TableHead className="text-right">Req Qty</TableHead>
                    <TableHead className="min-w-[100px]">PO No</TableHead>
                    <TableHead>Supplier</TableHead>
                    <TableHead className="text-right">Ordered</TableHead>
                    <TableHead className="text-right bg-blue-50">PO Price</TableHead>
                    <TableHead className="text-right">Received</TableHead>
                    <TableHead className="text-right bg-green-50">Actual Price</TableHead>
                    <TableHead className="text-right">Variance</TableHead>
                    <TableHead className="text-center">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={11} className="text-center py-8">
                        <div className="flex items-center justify-center gap-2">
                          <RefreshCw className="w-5 h-5 animate-spin" />
                          Loading procurement data...
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : filteredItems.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={11} className="text-center py-12">
                        <Package className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                        <p className="text-muted-foreground font-medium">No procurement records found</p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredItems.map((item) => {
                      const StatusIcon = statusConfig[item.procurement_status]?.icon || Clock;
                      return (
                        <TableRow key={item.indent_id} className="hover:bg-muted/30">
                          <TableCell>
                            <div>
                              <Badge variant="outline" className="font-mono text-xs">{item.indent_number}</Badge>
                              <p className="text-xs text-muted-foreground mt-1">{formatDate(item.indent_date)}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="max-w-[150px]">
                              <p className="font-medium truncate" title={item.item_name}>{item.item_name}</p>
                              {item.linked_wo && (
                                <Badge variant="secondary" className="text-xs mt-1">WO: {item.linked_wo}</Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            {formatNumber(item.qty_required)} <span className="text-xs text-muted-foreground">{item.unit}</span>
                          </TableCell>
                          <TableCell>
                            {item.po_number ? (
                              <Badge variant="outline" className="font-mono text-xs">{item.po_number}</Badge>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell className="max-w-[120px] truncate" title={item.supplier_name}>
                            {item.supplier_name || '-'}
                          </TableCell>
                          <TableCell className="text-right">
                            {item.po_ordered_qty > 0 ? formatNumber(item.po_ordered_qty) : '-'}
                          </TableCell>
                          <TableCell className="text-right bg-blue-50/50 font-medium">
                            {item.po_approved_price > 0 ? formatCurrency(item.po_approved_price) : '-'}
                          </TableCell>
                          <TableCell className="text-right">
                            {item.received_qty > 0 ? (
                              <div>
                                <span className="font-medium text-green-600">{formatNumber(item.received_qty)}</span>
                                {item.pending_qty > 0 && (
                                  <p className="text-xs text-orange-600">Pending: {formatNumber(item.pending_qty)}</p>
                                )}
                              </div>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right bg-green-50/50 font-medium">
                            {item.actual_purchase_price > 0 ? formatCurrency(item.actual_purchase_price) : '-'}
                          </TableCell>
                          <TableCell className="text-right">
                            {item.has_price_variance ? (
                              <div className={getVarianceColor(item.price_variance)}>
                                <div className="flex items-center justify-end gap-1">
                                  {item.price_variance > 0 ? (
                                    <TrendingUp className="w-3 h-3" />
                                  ) : (
                                    <TrendingDown className="w-3 h-3" />
                                  )}
                                  <span className="font-medium">{item.price_variance_percent?.toFixed(1)}%</span>
                                </div>
                                <p className="text-xs">{formatCurrency(item.price_variance)}</p>
                              </div>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge className={`${statusConfig[item.procurement_status]?.color} flex items-center gap-1 justify-center`}>
                              <StatusIcon className="w-3 h-3" />
                              {statusConfig[item.procurement_status]?.label}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
          {/* Bug 4 fix: paginator — preserves active status_filter across page changes */}
          {reportData?.pagination && reportData.pagination.total_pages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <p className="text-sm text-muted-foreground" data-testid="procurement-paginator-summary">
                Showing page {reportData.pagination.page} of {reportData.pagination.total_pages}
                {' · '}
                {reportData.pagination.total_count} indent{reportData.pagination.total_count === 1 ? '' : 's'} total
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={loading || page <= 1}
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  data-testid="procurement-prev-page-btn"
                >
                  Previous
                </Button>
                <span className="text-sm font-mono px-2" data-testid="procurement-current-page">
                  {reportData.pagination.page} / {reportData.pagination.total_pages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={loading || page >= reportData.pagination.total_pages}
                  onClick={() => setPage(p => Math.min(reportData.pagination.total_pages, p + 1))}
                  data-testid="procurement-next-page-btn"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>
  );
}

// Standalone page wrapper
export default function ProcurementTrackingPage() {
  return (
    <DashboardLayout>
      <ProcurementTrackingContent />
    </DashboardLayout>
  );
}
